import { Component, computed, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { $state } from 'app/ajs-upgraded-providers';
import { delay, of } from 'rxjs';
import { LoaderService, ScreenOrientationService, ToolbarEventsService } from 'src/app/core';
import { GridDownloadDialogService } from 'src/app/dialogs';
import { GridSavedState, GridStateService } from 'src/app/features/items/services/grid-state.service';
import { GridClientSideComponent, GridFixedButtons, headersTranslate } from 'src/app/shared/components';
import { DataExport, TableData } from 'src/app/shared/model/model';
import { SharedModule } from 'src/app/shared/shared.module';
import { Vat } from 'tilby-models';
import { filterableVatRatesColumnsWithSearchBar, VatRatesFe } from '../vat-rates-model';
import { RowClickedEvent } from 'ag-grid-community';
import { subscribeInComponent } from "src/app/shared/angular-utils";
import { CliccableActionItems, GridClickableButtonItemsComponent } from 'src/app/shared/components/grid/grid-clickable-button-items/grid-clickable-button-items.component';
import { autobind } from 'src/app/models/decorators.model';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { mobileCheck } from 'src/utilities';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';

@Component({
  selector: 'app-vat-rates-showcase',
  standalone: true,
  imports: [ SharedModule ],
  templateUrl: './vat-rates-showcase.component.html',
  styleUrl: './vat-rates-showcase.component.scss'
})
export class VatRatesShowcaseComponent  implements OnInit {

  private readonly state = inject($state);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly gridStateService = inject(GridStateService);
  private readonly loaderService = inject(LoaderService);
  private readonly gridDownloadDialogService = inject(GridDownloadDialogService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly screenOrientationService = inject(ScreenOrientationService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  
  protected isMobile = mobileCheck();
  protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));

  private isSelected = signal(false); 
  
  @ViewChild('VatRateGridComponent', {static: true}) gridRef!: GridClientSideComponent;
  protected gridPath = this.state.$current.name.split('.').pop() === 'showcase';
  query = {
      pagination: false,
      orderby_asc: "id",
  };
  value = "";
  exportFormatterOptions?: (...any: any[]) => any;
  tableData: TableData[] = [
    {       
      rowData$: null,
      dataType: new VatRatesFe(),
      headersTranslate: headersTranslate.vat_rates_showcase
    }
  ];
  fixedButtons: GridFixedButtons = {
    checkbox: {visible: true, lockPosition: 'left'},
    customButton: {
      visible: true, 
      cellRenderer: GridClickableButtonItemsComponent, 
      lockPosition: 'right',
      cellRendererParams: {
        showEdit: true
      }
    }
  };

  private movColumnsSwitch = false;
  private isAdvancedFilterActive = false;
  private savedState: GridSavedState | null = null;

  constructor() {
    effect(() => {
      [
        this.isLandscape(),
      ];

      setTimeout(() => {
        this.createToolbarButtons();
      }, 10)
    });

    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch(event.type) {
        case 'toolbar.clear_search_bar':
          return this.resetSearchBar();
        case 'toolbar.search':
          return this.gridRef.onFilterTextBoxChanged(event.search, filterableVatRatesColumnsWithSearchBar);
        case 'grid.open_tool_panel':
          return this.gridRef.openToolPanel(event.panel);
        default:
          break;
      }
    });

    this.initFilters();
  }

  resetSearchBar() {
    this.toolbarEventsService.searchBarValue$.next('');
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
    this.toolbarEventsService.events.next({ type: 'toolbar.search', search: '' });
  }

  private initFilters() {
    this.savedState = this.gridStateService.getState("vat_rates") || null;
    this.toolbarEventsService.searchBarValue$.next(this.savedState?.searchBarValue || '');
  }

  ngOnInit(): void {
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
  }

  onGridReady() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      // Ripristino filtri
      if(this.savedState) {
        if(this.savedState.filterModel) {
          this.gridRef.gridApi?.setFilterModel(this.savedState.filterModel);

          const model = this.savedState?.filterModel;

          for (const colId of Object.keys(model)) {
            const filterInstance = await api.getColumnFilterInstance(colId);
            
            if (filterInstance) {
              filterInstance.setModel(model[colId]);
            }
          }
        }
        
        if(this.savedState.paginationPage) {
          this.gridRef.gridApi?.paginationGoToPage(this.savedState.paginationPage);
        }
  
        this.toolbarEventsService.events.next({ type: 'toolbar.search', search: this.toolbarEventsService.searchBarValue$.value });
        api.onFilterChanged();
      }

      this.updateTable();
    })
  }

  onFilterChangedEmitter(event: any): void {
    this.saveGridState();
    this.createToolbarButtons();
  }

  saveGridState() {
    if (this.gridRef && this.gridRef.gridApi) {
      this.gridStateService.saveState("vat_rates", {
        filterModel: this.gridRef.gridApi?.getFilterModel(),
        paginationPage: this.gridRef.gridApi?.paginationGetCurrentPage(),
        searchBarValue: this.toolbarEventsService.searchBarValue$.value
      });
    }
  }

  
  private getToolbarConfig() {
    const trueSignal = signal(true);
    
    const allButtons: GeneralInputButton[] = [
      {
        name: 'clear_filters',
        isIt: () => Object.keys(this.gridRef?.gridApi?.getFilterModel() || {}).length > 0,
        icon: signal('filter_alt_off'),
        label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
        click: () => this.clearGridFilters()
      },
      {
        name: 'add_new_vat_rate',
        isIt: trueSignal,
        icon: signal('playlist_add'),
        label: () => 'TOPBAR.ACTIONS.NEW_VAT_RATE',
        click: () => this.state.go("app.new.items.vat-rate-management.new")
      }
    ];

    const fixedButtons: GeneralInputButton[] = [
      {
        name: 'columns_panel',
        isIt: trueSignal,
        icon: signal('view_week'),
        label: signal('TOPBAR.ACTIONS.COLUMN_SELECTED'),
        click: () => this.gridRef.openToolPanel('columns')
      },
      {
        isIt: trueSignal,
        name: 'movable_columns',
        icon: signal('drag_indicator'),
        label: () => this.movColumnsSwitch ? 'TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT': 'TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT',
        click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
      },
      {
        isIt: trueSignal,
        name: 'save_columns_position',
        icon: signal('save'),
        label: signal('TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE'),
        click: () => this.gridRef.openDialogSaveColumnsPreference()
      },
      {
        isIt: () => !this.isMobile,
        name: 'advanced_filter',
        icon: () => this.isAdvancedFilterActive ? 'filter_alt_off' : 'filter_list_alt',
        label: () => this.isAdvancedFilterActive ? 'TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED',
        click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
      },
      {
        isIt: trueSignal,
        name: 'upload',
        icon: signal('upload'),
        label: signal('TOPBAR.ACTIONS.IMPORT'),
        click: () => this.goToImporter()
      },
      {
        isIt: trueSignal,
        name: 'update',
        icon: signal('update'),
        label: signal('ITEMS.TOPBAR_ITEMS.UPDATE'),
        click: () => this.updateTable()
      },
      {
        name: 'export',
        isIt: () => true,
        icon: signal('download'),
        label: signal('TOPBAR.ACTIONS.EXPORT'),
        click: () => this.triggerExport()
      },
      {
        isIt: trueSignal,
        name: 'settings_backup_restore',
        icon: signal('settings_backup_restore'),
        label: signal('TOPBAR.ACTIONS.RESET_TABLE'),
        click: () => this.gridRef.resetLayout()
      }
    ];

    return { allButtons, fixedButtons };
  }

  createToolbarButtons() {
    const { allButtons, fixedButtons } = this.getToolbarConfig();
    this.toolbarLayoutService.updateToolbarLayout(allButtons, fixedButtons);
  }

  clearGridFilters() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      const model = this.gridStateService.getState("vat_rates")?.filterModel;

      if(model) {
        for (const colId of Object.keys(model)) {
          const filterInstance = await api.getColumnFilterInstance(colId);
          
          if (filterInstance) {
            filterInstance.setModel(null);
          }
        }
      }

      api.onFilterChanged();
    })
  }
  
  @autobind
  onExport(dataExport: DataExport) {
    // this.gridRef?.onBtnExport(dataExport);
    const rowSelected = !!this.gridRef.getSelectedCount();
    this.gridRef?.clientSideExport(dataExport, rowSelected);
  }

  goToImporter() {
    this.state.go('app.new.file_importer');
  }

  rowClicked({data}: RowClickedEvent) {
    // this.movColumnsSwitch && this.gridRef.columnMovableSwitch(this.movColumnsSwitch = false);
    if (this.state.router.globals.transition) {
      return; 
    }
    this.goToDetails(data);
  }

  async triggerExport() {
    const res = await this.gridDownloadDialogService.openDialog('vat_rates', {
      data: this.tableData.map(tableData => ({
        ...tableData,
        sortFilterQuery: this.gridRef.getSortFilterQuery(),
        countSelectedRows: this.gridRef.gridApi?.getSelectedRows().length
      }))
    });

    if (res) {
      this.onExport(res);
    }
  }
      
  updateTable() {
    this.tableData[0].rowData$ = of(this.itemDataCatalogService.getDataByKey('vat') || []).pipe(delay(100));
  }

  actions({ action, data }: CliccableActionItems<Vat>) {
    if(data) {
      switch (action) {
        case "details": 
          return this.goToDetails(data);
      }
    }
  }

  goToDetails(row: Vat) {
    if(!row || row.id == null) return;
    this.state.go("app.new.items.vat-rate-management.details", {vatRateId: row.id });
    this.loaderService.show();
  }

  handleSelectionChange(selected: boolean) {
    this.isSelected.set(selected);
    this.createToolbarButtons(); 
  }
}
